// Copyright ©️ 2026 eVolve MEP, LLC

import React from 'react';

import type { Facility } from '@evolvemep/api-ts';
import { Text } from '@mantine/core';
import type { ColDef } from 'ag-grid-enterprise';

import { CellRenderer } from 'components/Mantine/CellRenderer';
import { GridMenu } from 'components/Mantine/GridMenu';
import { lockedColDef } from 'helpers/ag-grid/baseColumnDef';
import type { UseState } from 'helpers/helper-types';
import { ActiveClosedStatusBadge } from 'modules/Admin/Facilities/ActiveClosedStatusBadge';
import type { FacilityActionModalState } from 'modules/Admin/Facilities/FacilityActionModals';

export const getFacilitiesColDef = ({
  actionFacility,
}: {
  actionFacility: UseState<FacilityActionModalState | null>;
}): ColDef<Facility>[] => [
  {
    field: 'facilityName',
    lockVisible: true,
    flex: 1,
    minWidth: 150,
    cellRenderer: CellRenderer<Facility, 'facilityName'>(({ data, node }) => (
      <Text className="link" onClick={() => node.setSelected(true, true)}>
        {data.facilityName}
      </Text>
    )),
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    headerName: 'City',
    field: 'city',
    sortable: false,
    width: 180,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    headerName: 'State',
    field: 'state',
    sortable: false,
    width: 100,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    headerName: 'Delivery Lead Time',
    field: 'deliveryLeadTime',
    width: 180,
    valueFormatter: ({ value }) => `${value} day${value === 1 ? '' : 's'}`,
    sortable: false,
  },
  {
    headerName: 'Status',
    field: 'isClosed',
    flex: 1,
    minWidth: 120,
    sortable: true,
    cellRenderer: CellRenderer<Facility, 'isClosed'>(({ value: isClosed }) => (
      <ActiveClosedStatusBadge isClosed={isClosed} />
    )),
  },
  {
    ...lockedColDef('right'),
    width: 64,
    cellRenderer: CellRenderer<Facility>(({ data: facility, node }) => (
      <GridMenu
        options={[
          { value: 'select', label: 'Select facility' },
          { value: 'open-close', label: (facility.isClosed ? 'Activate' : 'Archive') + ' facility' },
          { value: 'delete', label: 'Delete' },
        ]}
        onMenuItemSelected={(menuItemSelected) => {
          if (menuItemSelected === 'select') {
            node.setSelected(true, true);
          } else {
            actionFacility({ action: menuItemSelected, facility });
          }
        }}
      />
    )),
  },
];
